import { comonDataApi } from '@/api/config-setup'
import InputFile from '@/components/form-control/InputFile'
import { CopyOneIcon, DatabaseAlerIcon } from '@/components/icons'
import { XLSX_ACEPT, XLSX_FORMAT } from '@/constant/common'
import { CONFIG_SETUP_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import { useAuthContext } from '@/features/auth/context'
import FooterSetup from '@/features/design-system/components/common/FooterSetup'
import SetUpHeaderModal from '@/features/design-system/components/common/SetUpHeaderModal'
import SetUpLayout from '@/features/design-system/components/common/SetUpLayout'
import { NotifyService } from '@/helpers'
import { DATA_PAGINATION, EtZone } from '@/types'
import { fileContentToBlob } from '@/ultils'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { saveAs } from 'file-saver'
import { memo, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import EtZoneTable from '../components/EtZoneTable'
import classes from './styles.module.scss'
import useSearchInterface from '../../../../../admin/hooks/interface/useSearchInterface'
import useValidateCurrentInterface from '@features/design-system/hooks/setup/useValidateCurrentInterface'
import useTranslation from '@/hooks/useTranslation'

interface Props {}

enum STEP {
  CHOOSE_FILE = 0,
  UPLOADING,
  UPLOAD_ERROR,
  REVIEW,
}

export const SetupZone = memo((props: Props) => {
  const { t } = useTranslation()
  
  const { interfaceId } = useAuthContext()

  const navigate = useNavigate()
  const [step, setStep] = useState(STEP.CHOOSE_FILE)
  const [etZone, setZone] = useState<DATA_PAGINATION<EtZone>>()
  const [isDeleted, setIsDeleted] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const { id } = useParams()
  const { data: allInterfaces } = useSearchInterface()

  const { isValidInterface, selectedInterface, isLoading } = useValidateCurrentInterface({
    currentInterfaceId: id,
    allInterfaces,
    redirectTo: OPERATIONS_ROUTES.index.to,
    autoRedirect: false,
  })


  const onBack = () => {
    navigate(CONFIG_SETUP_ROUTES.setupSecond.to(interfaceId+""))
  }

  const onErrors = () => {}

  const onDownload = async () => {
    setIsDownloading(true)
    try {
      await comonDataApi.getEtZoneTemplate(Number(interfaceId)).then((res) => {
        saveAs(res.data, 'ETZone_Template.xlsx')
      })
    } catch (error: any) {
      toast.error('Failed to download the template. Please try again later.')
    } finally {
      setIsDownloading(false)
    }
  }

  const onTryAgain = () => {
    setStep(STEP.CHOOSE_FILE)
  }

  useEffect(() => {
    comonDataApi.deleteEtZoneFile(Number(interfaceId)).then(({ data }) => {
      if (data.success) {
        toast.success(data.message || '')
        setIsDeleted(data.success)
      }
    })
  }, [interfaceId])

  const onChangeFile = async (file: File[]) => {
    if (!file?.length) return
    const data = new FormData()
    data.append('file', file[0])
    setStep(STEP.UPLOADING)
    await comonDataApi
      .uploadEtZoneFile(Number(interfaceId), data, isDeleted)
      .then(({ data }) => {
        if (data.success) {
          setZone(data.data)
          setStep(STEP.REVIEW)
        } else {
          setStep(STEP.UPLOAD_ERROR)
          NotifyService.error('Upload file error')
          if (data.result) {
            saveAs(
              fileContentToBlob(
                data.result.fileContents,
                data.result.contentType,
              ),
              data.result.fileDownloadName,
            )
          }
        }
      })
      .catch(() => {
        NotifyService.error('Upload file error')
        setStep(STEP.UPLOAD_ERROR)
      })
  }

  const render = () => {
    switch (step) {
      case STEP.CHOOSE_FILE:
        return (
          <Box className={classes.root}>
            <Typography className={classes.description} mt="52px">
              Data 5 of 6
            </Typography>
            <Typography className={classes.pageTitle}>ET Zone</Typography>
            <Stack mt="80px" alignItems="flex-start">
              <Typography className={classes.stepTitle}>
                Step 1. Fill in your data in the XLSX template
              </Typography>
              <Box className={`${classes.actions} ${classes.actionsDow}`}>
                {
                  isDownloading
                    ? <LoadingButton
                      className={classes.btnDownload}
                      startIcon={<CopyOneIcon />}
                      loading
                      variant="secondary">
                      Download Excel Template
                    </LoadingButton>
                    : <Button
                      color="secondary"
                      variant="secondary"
                      className={classes.btnDownload}
                      onClick={onDownload}
                      startIcon={<CopyOneIcon />}
                    >
                      Download Excel Template
                    </Button>
                }
              </Box>
              <Typography className={classes.stepTitle}>
                Step 2. Upload the XLSX
              </Typography>
              <Box
                sx={{
                  ...(!isDeleted && {
                    opacity: 0.5,
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }),
                }}
              >
                <InputFile
                  accept={XLSX_ACEPT}
                  formats={XLSX_FORMAT}
                  onChange={onChangeFile}
                />
              </Box>
            </Stack>
          </Box>
        )
      case STEP.UPLOADING:
        return (
          <Box className={classes.root}>
            <Typography className={classes.description} mt="52px">
              Data 5 of 6
            </Typography>
            <Typography className={classes.pageTitle}>ET Zone</Typography>
            <CircularProgress className={classes.progress} size="64px" />
            <Typography className={classes.title}>Processing Data</Typography>
            <Typography className={classes.description}>
              Please do not close the window
            </Typography>
          </Box>
        )
      case STEP.UPLOAD_ERROR:
        return (
          <Box className={classes.root}>
            <Typography className={classes.description} mt="52px">
              Data 5 of 6
            </Typography>
            <Typography className={classes.pageTitle}>ET Zone</Typography>
            <DatabaseAlerIcon className={classes.errorIcon} />
            <Typography className={classes.title}>
              Unable to import data
            </Typography>
            <Typography className={classes.description}>
              The file is not in the right format, or has typo errors
            </Typography>
            <Box className={classes.actions}>
              <Button
                variant="contained"
                className={classes.btnTry}
                onClick={onTryAgain}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        )
      case STEP.REVIEW:
        return (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className={classes.Table}
            >
              <Typography className={classes.TableTitle}>
                Review Micro Climate Data
              </Typography>
              <Stack alignItems="center">
                <Button
                  variant="contained"
                  className={classes.btnTry}
                  onClick={onBack}
                >
                  Done
                </Button>
              </Stack>
            </Stack>
            <EtZoneTable data={etZone} />
          </>
        )
    }
  }
  return (
    <SetUpLayout
      title={ t('formsLabelsMicroClimate') }
      header={
        <SetUpHeaderModal
          title="Common Data"
          onCancel={step === STEP.UPLOADING ? onErrors : onBack}
        />
      }
      footer={
        step === STEP.REVIEW && (
          <Stack p="25px 32px">
            <FooterSetup
              title={`${etZone?.totalCount || 0} rows of data detected`}
              subtitle="Review the data before proceeding"
            />
          </Stack>
        )
      }
    >
      {render()}
    </SetUpLayout>
  )
})

export default SetupZone
